import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { showSuccessToast, showErrorToast } from '@/utils/toast-utils';
import { ImageUpload } from './ImageUpload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { User, Building, GraduationCap, Info, Calendar, Calculator } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AvailabilityCalendar } from './AvailabilityCalendar';
import { LabeledSelect } from '@/components/shared/LabeledSelect';
import { getNutritionistProfile } from '@/lib/apiService';
import { Loader2 } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

export const ProfileSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    bio: "",
    company: "",
    phoneNumber: "",
    phoneCountryCode: "+1",
    secondaryPhone: "",
    email: "",
    address: "",
    practiceName: "",
    referralSource: "",
    researchArea: "",
    location: "",
    accolades: "",
    linkedin: "",
    blog: "",
    profilePhoto: "",
    clinicLogo: "",
    eer_formula: "",
    nutrient_guideline: "",
    qualification: "",
    experienceYears: "",
    specialization: []
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (field: string, imageUrl: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: imageUrl
    }));
  };

  // Handler for phone input
  const handlePhoneChange = (value: string, data: any) => {
    let phonePart = value;
    if (data.dialCode && value.startsWith(data.dialCode)) {
      phonePart = value.slice(data.dialCode.length);
    } else if (data.dialCode && value.startsWith('+' + data.dialCode)) {
      phonePart = value.slice(('+' + data.dialCode).length);
    }
    setPhone(phonePart);
    const newCountryCode = '+' + data.dialCode;
    setCountryCode(newCountryCode);
    setProfile(prev => ({
      ...prev,
      phoneNumber: phonePart,
      phoneCountryCode: newCountryCode
    }));
  };

  // Fetch nutritionist profile on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      setIsFetching(true);
      try {
        const response = await getNutritionistProfile();
        if (response.success && response.data) {
          const data = response.data;
          const phoneCountryCode = data.phoneCountryCode || "+1";
          const phoneNumber = data.phone || "";
          
          // Set phone and country code state
          setCountryCode(phoneCountryCode);
          setPhone(phoneNumber);
          
          setProfile({
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            bio: "",
            company: data.qualification || "",
            phoneNumber: phoneNumber,
            phoneCountryCode: phoneCountryCode,
            secondaryPhone: "",
            email: data.email || "",
            address: "",
            practiceName: "",
            referralSource: "",
            researchArea: data.specialization?.join(", ") || "",
            location: "",
            accolades: data.qualification || "",
            linkedin: "",
            blog: "",
            profilePhoto: data.profileImageUrl || "",
            clinicLogo: "",
            eer_formula: "",
            nutrient_guideline: "",
            qualification: data.qualification || "",
            experienceYears: data.experienceYears?.toString() || "",
            specialization: data.specialization || []
          });
        }
      } catch (error) {
        console.error('Failed to fetch nutritionist profile:', error);
        showErrorToast(error, "Failed to load profile");
      } finally {
        setIsFetching(false);
      }
    };

    fetchProfile();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      showSuccessToast("Profile updated", "Your profile information has been updated successfully.");
    }, 1000);
  };

  const referralOptions = ["Social Media", "Friend/Colleague", "Search Engine", "Conference", "Professional Network", "Advertisement", "Other"];
  
  const eerFormulaOptions = [
    { value: "Henry Oxford 2005", label: "Henry Oxford 2005" },
    { value: "Institute of Medicine 2005", label: "Institute of Medicine 2005" },
    { value: "Katch McArdle 1996", label: "Katch McArdle 1996" },
    { value: "Mifflin St. Jeor 1990", label: "Mifflin St. Jeor 1990" },
    { value: "Schofield 1985", label: "Schofield 1985" },
    { value: "Cunningham 1980", label: "Cunningham 1980" },
    { value: "Harris-Benedict 1919", label: "Harris-Benedict 1919" },
    { value: "Ten Haaf 2014", label: "Ten Haaf 2014" }
  ];

  const nutrientGuidelineOptions = [
    { value: "UK SACN 2017 / COMA", label: "UK SACN 2017 / COMA" },
    { value: "US IOM 2010 (Institute of Medicine)", label: "US IOM 2010 (Institute of Medicine)" },
    { value: "ICMR-NIN (India)", label: "ICMR-NIN (India)" },
    { value: "EFSA (Europe)", label: "EFSA (Europe)" },
    { value: "AUS: NHMRC (Australia/New Zealand)", label: "AUS: NHMRC (Australia/New Zealand)" },
    { value: "WHO/FAO International", label: "WHO/FAO International" }
  ];

  if (isFetching) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
        <span className="text-lg text-muted-foreground">Loading profile...</span>
      </div>
    );
  }

  return <div>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Account Information Section */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-cs-light px-6 py-4 flex items-center gap-2 border-b border-gray-200">
            <User className="h-5 w-5 text-cs-primary" />
            <h2 className="text-xl font-medium text-cs-foreground">Account Information</h2>
          </div>
          
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left column - Form fields */}
              <div className="space-y-6 flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input id="firstName" name="firstName" value={profile.firstName} onChange={handleChange} placeholder="Enter your first name" />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input id="lastName" name="lastName" value={profile.lastName} onChange={handleChange} placeholder="Enter your last name" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" name="email" type="email" value={profile.email} onChange={handleChange} placeholder="Enter your email address" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number (Primary)</Label>
                  <PhoneInput
                    country={'us'}
                    value={countryCode.replace('+', '') + phone}
                    onChange={handlePhoneChange}
                    countryCodeEditable={false}
                    inputProps={{
                      name: 'phoneNumber',
                      required: false,
                      autoFocus: false,
                    }}
                    containerClass="h-12 text-base rounded-md py-2 bg-background flex items-center border border-input"
                    inputClass="!outline-0 !ring-0 flex-1 !h-[50px] !border-t !border-b !border-r !px-16"
                    buttonClass="!bg-white !px-2 !border-r !border-gray-300 !rounded-l-md !border-t-0 !border-b-0"
                    dropdownClass="!border-gray-300"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="secondaryPhone">Phone Number (Secondary)</Label>
                  <Input id="secondaryPhone" name="secondaryPhone" value={profile.secondaryPhone} onChange={handleChange} placeholder="Enter your secondary phone number (optional)" />
                </div>
              </div>
              
              {/* Right column - Profile Photo */}
              <div className="md:w-1/3 flex flex-col justify-start">
                <div className="flex items-center space-x-2 mb-2">
                  <Label className="block">Profile Photo</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="p-1.5 inline-flex items-center justify-center rounded-full hover:bg-gray-100">
                          <Info className="h-4 w-4 text-gray-500" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm">This image will be shown on your dashboard and visible to clients.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <ImageUpload id="profile-photo" label="Profile Photo" currentImage={profile.profilePhoto} onImageChange={url => handleImageChange("profilePhoto", url)} />
              </div>
            </div>
          </div>
        </div>

        {/* Practice Details Section */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-cs-light px-6 py-4 flex items-center gap-2 border-b border-gray-200">
            <Building className="h-5 w-5 text-cs-primary" />
            <h2 className="text-xl font-medium text-cs-foreground">Practice Details</h2>
          </div>
          
          <div className="p-6">
            {/* Practice Name and Logo - Aligned like the Account Info section */}
            <div className="flex flex-col md:flex-row gap-6 mb-6">
              {/* Left column - Form fields */}
              <div className="space-y-6 flex-1">
                <div className="space-y-2">
                  <Label htmlFor="practiceName">Practice/Clinic Name</Label>
                  <Input id="practiceName" name="practiceName" value={profile.practiceName} onChange={handleChange} placeholder="Enter your practice or clinic name" />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Physical Address</Label>
                  <Input id="address" name="address" value={profile.address} onChange={handleChange} placeholder="Enter your physical address" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="referralSource">Referral Source</Label>
                  <Select value={profile.referralSource} onValueChange={value => handleSelectChange("referralSource", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="How did you hear about us?" />
                    </SelectTrigger>
                    <SelectContent>
                      {referralOptions.map(option => <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* EER Formula Dropdown */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="eer_formula" className="flex items-center gap-2">
                      <Calculator className="h-4 w-4 text-gray-500" />
                      EER Formula
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="p-1.5 inline-flex items-center justify-center rounded-full hover:bg-gray-100">
                            <Info className="h-4 w-4 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm">This formula will be used to calculate BMR/EER during meal planning.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select value={profile.eer_formula} onValueChange={value => handleSelectChange("eer_formula", value)}>
                    <SelectTrigger id="eer_formula">
                      <SelectValue placeholder="Select EER formula" />
                    </SelectTrigger>
                    <SelectContent>
                      {eerFormulaOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Nutrient Target Guidelines Dropdown */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="nutrient_guideline" className="flex items-center gap-2">
                      <Info className="h-4 w-4 text-gray-500" />
                      Nutrient Target Guidelines (DRV)
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="p-1.5 inline-flex items-center justify-center rounded-full hover:bg-gray-100">
                            <Info className="h-4 w-4 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm">Defines macro ratio ranges and RDA/DRI micronutrient targets.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select value={profile.nutrient_guideline} onValueChange={value => handleSelectChange("nutrient_guideline", value)}>
                    <SelectTrigger id="nutrient_guideline">
                      <SelectValue placeholder="Select nutrient guideline" />
                    </SelectTrigger>
                    <SelectContent>
                      {nutrientGuidelineOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Right column - Practice Logo */}
              <div className="md:w-1/3 flex flex-col justify-start">
                <div className="flex items-center space-x-2 mb-2">
                  <Label className="block">Practice Logo</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="p-1.5 inline-flex items-center justify-center rounded-full hover:bg-gray-100">
                          <Info className="h-4 w-4 text-gray-500" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm">This is the logo that will appear on client invoices.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <ImageUpload id="clinic-logo" label="Clinic Logo" currentImage={profile.clinicLogo} onImageChange={url => handleImageChange("clinicLogo", url)} />
                <p className="text-sm text-gray-600 mt-2">Upload your clinic or business logo to enhance your professional branding.</p>
              </div>
            </div>
          </div>
        </div>

        {/* My Availability Section */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-cs-light px-6 py-4 flex items-center gap-2 border-b border-gray-200">
            <Calendar className="h-5 w-5 text-cs-primary" />
            <h2 className="text-xl font-medium text-cs-foreground">My Availability</h2>
          </div>
          
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              Set your available dates and times for client appointments. Clients will be able to book appointments during these times.
            </p>
            
            {/* We'll leave this section empty as it will be populated by the AvailabilityCalendar component in the tab content */}
          </div>
        </div>

        {/* Professional Details Section - Updated with two-column layout */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-cs-light px-6 py-4 flex items-center gap-2 border-b border-gray-200">
            <GraduationCap className="h-5 w-5 text-cs-primary" />
            <h2 className="text-xl font-medium text-cs-foreground">Professional Details</h2>
          </div>
          
          <div className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left column - Education and qualifications */}
              <div className="space-y-6 md:w-1/2">
                <div className="space-y-2">
                  <Label htmlFor="company">Degree / University</Label>
                  <Input id="company" name="company" value={profile.company} onChange={handleChange} placeholder="Where did you receive your degree/certification" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="researchArea">Area of Research</Label>
                  <Input id="researchArea" name="researchArea" value={profile.researchArea} onChange={handleChange} placeholder="Enter your area of specialization" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accolades">Accolades / Accomplishments</Label>
                  <Textarea id="accolades" name="accolades" value={profile.accolades} onChange={handleChange} placeholder="List your professional accomplishments" rows={3} />
                </div>
              </div>
              
              {/* Right column - Bio and social links */}
              <div className="space-y-6 md:w-1/2">
                <div className="space-y-2">
                  <Label htmlFor="bio">Short Bio</Label>
                  <Textarea id="bio" name="bio" value={profile.bio} onChange={handleChange} placeholder="Write a brief description of your professional background" rows={4} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <Input id="linkedin" name="linkedin" value={profile.linkedin} onChange={handleChange} placeholder="Enter your LinkedIn profile URL" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="blog">Blog/Website</Label>
                  <Input id="blog" name="blog" value={profile.blog} onChange={handleChange} placeholder="Enter your blog or website URL" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info Section */}
        <div className="pt-4">
          <Button type="submit" className="bg-cs-primary" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>;
};
